// [CCTOWER-2441] 供应商报价模板配置 — 前端适配层（对接 price-service）

import {
  batchDisableSupplierQuotationTemplate,
  batchEnableSupplierQuotationTemplate,
  bindSupplierQuotationTemplateSuppliers,
  deleteSupplierQuotationTemplate,
  getSupplierQuotationTemplate,
  initializeStandardSupplierQuotationTemplate,
  listSupplierQuotationFieldCatalog,
  listSupplierQuotationTemplateSuppliers,
  matchSupplierQuotationTemplate,
  pageSupplierQuotationTemplate,
  saveSupplierQuotationTemplate,
  updateSupplierQuotationTemplate
} from '@/api/sass/supplier-quotation-template'
import type { SupplierQuotationTemplateVO } from '@/api/sass/supplier-quotation-template/types'
import { feeManagementApi } from '@/api/basic-data/fee-management'
import type { FeeManagementVO } from '@/api/basic-data/fee-management'
import {
  abandonSupplierQuotation,
  getSupplierQuotation,
  getSupplierQuotationByToken,
  pageSupplierQuotation,
  saveSupplierQuotationDraft,
  submitSupplierQuotation
} from '@/api/sass/supplier-quotation'
import { fetchSupplierEvaluationPage } from '@/api/supplier-evaluation'
import type {
  QuotationTemplate,
  QuotationTemplateType,
  TemplateFieldConfig,
  SupplierAssociation,
  SupplierQuotation
} from './types'
import { TYPE_DISPLAY_ORDER } from './types'
import { t } from '@/i18n'

function toId(value: string | number | undefined | null): string {
  return value == null ? '' : String(value)
}

function mapTemplateListItem(item: any): QuotationTemplate & { fieldCount?: number; isStandard?: boolean } {
  return {
    id: toId(item.id),
    templateNo: item.templateNo || '',
    templateName: item.templateName || '',
    businessType: item.businessType || undefined,
    templateTypes: (item.templateTypes || []) as QuotationTemplateType[],
    status: Number(item.status) === 0 ? 0 : 1,
    fields: [],
    fieldCount: item.fieldCount || 0,
    supplierCount: Number(item.supplierCount) || 0,
    supplierNames: item.supplierNames || [],
    updatedAt: item.dateModified || item.dateCreated || '',
    createdAt: item.dateCreated || '',
    isStandard: Number(item.isStandard) === 1
  }
}

function mapField(field: any): TemplateFieldConfig {
  return {
    fieldCode: field.fieldCode,
    fieldName: field.fieldName,
    fieldType: field.fieldType,
    required: !!field.required,
    visible: field.visible == null ? true : !!field.visible,
    locked: !!field.locked,
    sortOrder: field.sortOrder ?? 0,
    options: field.options,
    groupType: field.groupType,
    subFields: field.subFields,
    requiredFeeIds: Array.isArray(field.requiredFeeIds)
      ? field.requiredFeeIds.map((id: any) => String(id))
      : []
  }
}

/**
 * 按「公共字段 → 业务组 TYPE_DISPLAY_ORDER → 组内 sortOrder」排序后，重写全局 sortOrder（1..n）。
 * 目录表 sort_order 是组内序号，不同组会撞号，不能只按数字重排，否则公共字段会插进港前字段中间。
 */
function normalizeFieldSort(fields: TemplateFieldConfig[]): TemplateFieldConfig[] {
  const sorted = [...fields].sort((a, b) => {
    const ao = a.groupType ? (TYPE_DISPLAY_ORDER[a.groupType] ?? 500) : 0
    const bo = b.groupType ? (TYPE_DISPLAY_ORDER[b.groupType] ?? 500) : 0
    if (ao !== bo) return ao - bo
    return (a.sortOrder ?? 0) - (b.sortOrder ?? 0)
  })
  return sorted.map((f, i) => ({ ...f, sortOrder: i + 1 }))
}

function mapTemplateDetail(detail: SupplierQuotationTemplateVO): QuotationTemplate & {
  supplierNames: string[]
  isStandard?: boolean
} {
  const fields = normalizeFieldSort((detail.fields || []).map(mapField))
  const supplierNames = (detail.suppliers || [])
    .map(s => s.supplierName)
    .filter(Boolean) as string[]
  return {
    id: toId(detail.id),
    templateNo: detail.templateNo || '',
    templateName: detail.templateName || '',
    businessType: (detail as any).businessType || undefined,
    templateTypes: (detail.templateTypes || []) as QuotationTemplateType[],
    status: Number(detail.status) === 0 ? 0 : 1,
    fields,
    supplierCount: detail.supplierCount ?? (detail.suppliers?.length || 0),
    supplierNames,
    updatedAt: detail.dateModified || detail.dateCreated || '',
    createdAt: detail.dateCreated || '',
    isStandard: Number(detail.isStandard) === 1
  }
}

/** 初始化标准模板（幂等，列表页进入时调用） */
export async function ensureStandardTemplate(): Promise<void> {
  await initializeStandardSupplierQuotationTemplate()
}

/** 分页查询模板列表 */
export async function fetchTemplates(params: {
  pageNum: number
  pageSize: number
  keyword?: string
  templateType?: string
  businessType?: string
  status?: number
}): Promise<{ records: QuotationTemplate[]; total: number }> {
  const page = await pageSupplierQuotationTemplate({
    pageNum: params.pageNum,
    pageSize: params.pageSize,
    keyword: params.keyword,
    businessType: params.businessType || params.templateType,
    templateType: params.templateType,
    status: params.status
  })
  return {
    records: (page?.records || []).map(mapTemplateListItem),
    total: page?.total || 0
  }
}

/** 获取模板详情 */
export async function fetchTemplateDetail(
  id: string
): Promise<(QuotationTemplate & { supplierNames: string[]; isStandard?: boolean }) | null> {
  const detail = await getSupplierQuotationTemplate(id)
  if (!detail) return null
  return mapTemplateDetail(detail)
}

/** 保存模板（新建/编辑） */
export async function saveTemplate(data: {
  id?: string
  templateName: string
  businessType: string
  templateTypes: QuotationTemplateType[]
  status: number
  fields: TemplateFieldConfig[]
  remark?: string
}): Promise<QuotationTemplate> {
  const orderedFields = normalizeFieldSort(data.fields)
  const payload = {
    templateName: data.templateName,
    businessType: data.businessType,
    templateTypes: data.templateTypes,
    remark: data.remark,
    status: data.status,
    fields: orderedFields.map(f => ({
      fieldCode: f.fieldCode,
      fieldName: f.fieldName,
      fieldType: f.fieldType,
      groupType: f.groupType,
      required: f.required ? 1 : 0,
      visible: f.visible === false ? 0 : 1,
      locked: f.locked ? 1 : 0,
      sortOrder: f.sortOrder,
      options: f.options,
      subFields: f.subFields,
      requiredFeeIds: f.requiredFeeIds || []
    }))
  }

  if (data.id) {
    await updateSupplierQuotationTemplate({ id: data.id, ...payload })
    const detail = await fetchTemplateDetail(data.id)
    return detail!
  }

  await saveSupplierQuotationTemplate(payload)
  return {
    id: '',
    templateNo: '',
    templateName: data.templateName,
    businessType: data.businessType,
    templateTypes: data.templateTypes,
    status: data.status === 0 ? 0 : 1,
    fields: orderedFields,
    supplierCount: 0,
    createdAt: '',
    updatedAt: ''
  }
}

/** 删除模板 */
export async function deleteTemplate(id: string): Promise<boolean> {
  return deleteSupplierQuotationTemplate(id)
}

/** 切换模板状态 */
export async function toggleTemplateStatus(
  id: string,
  status: 0 | 1
): Promise<boolean> {
  if (status === 1) {
    return batchEnableSupplierQuotationTemplate([id])
  }
  return batchDisableSupplierQuotationTemplate([id])
}

/** 标准模板字段缓存（同一次页面会话内复用） */
let standardFieldsCache: TemplateFieldConfig[] | null = null

/** 拉取字段目录（先 initialize 再 list-field-catalog） */
async function loadStandardFields(): Promise<TemplateFieldConfig[]> {
  if (standardFieldsCache) {
    return standardFieldsCache
  }
  await initializeStandardSupplierQuotationTemplate()
  // 不传类型拿到全量目录，前端再按勾选过滤（带缓存）
  const fields = await listSupplierQuotationFieldCatalog()
  if (!fields?.length) {
    throw new Error(t('supplierQuotationConfig.api.catalogEmpty'))
  }
  standardFieldsCache = fields.map(mapField)
  return standardFieldsCache
}

/** 将模块码展开为可匹配目录 groupType（兼容 FCL/LCL） */
function expandCatalogGroupTypes(types: QuotationTemplateType[]): Set<string> {
  const set = new Set<string>(types.map(String))
  if (set.has('SEA')) {
    set.add('FCL')
    set.add('LCL')
  }
  if (set.has('AIR')) {
    set.add('AIR_FCL')
    set.add('AIR_LCL')
  }
  return set
}

/** 按勾选模板配置模块，从字段目录过滤默认字段 */
export async function getDefaultFields(types: QuotationTemplateType[]): Promise<TemplateFieldConfig[]> {
  if (!types.length) {
    return []
  }
  const typeSet = expandCatalogGroupTypes(types)
  const all = await loadStandardFields()
  const filtered = all.filter(f => !f.groupType || typeSet.has(f.groupType))
  // 与 normalizeFieldSort 同一规则：公共字段在前，再按组/组内目录 sort_order
  return normalizeFieldSort(JSON.parse(JSON.stringify(filtered)))
}

/** 获取已关联供应商列表 */
export async function fetchSuppliersByTemplate(templateId: string): Promise<SupplierAssociation[]> {
  const list = await listSupplierQuotationTemplateSuppliers(templateId)
  // 按供应商去重（多类型绑定时同一供应商可能多行）
  const map = new Map<string, SupplierAssociation>()
  for (const item of list || []) {
    const supplierId = toId(item.supplierId)
    if (!map.has(supplierId)) {
      map.set(supplierId, {
        id: toId(item.id),
        supplierId,
        supplierName: item.supplierName || '',
        templateId,
        templateType: item.templateType as QuotationTemplateType
      })
    }
  }
  return Array.from(map.values())
}

/** 保存供应商关联（按业务类型绑定，空数组清空） */
export async function saveSupplierAssociations(
  templateId: string,
  suppliers: { supplierId: string; supplierName: string }[],
  _templateTypes?: QuotationTemplateType[]
): Promise<boolean> {
  const detail = await getSupplierQuotationTemplate(templateId)
  const businessType = (detail as any)?.businessType as string | undefined
  if (!businessType) {
    throw new Error(t('supplierQuotationConfig.api.noTemplateTypes'))
  }
  const binds = suppliers.map(s => ({
    supplierId: s.supplierId,
    supplierName: s.supplierName,
    templateType: businessType
  }))
  return bindSupplierQuotationTemplateSuppliers({
    templateId,
    suppliers: binds
  })
}

/** 查询供应商列表（客商中心供应商评价分页，支持空关键词拉首屏） */
export async function searchSuppliers(keyword: string): Promise<{ id: string; name: string }[]> {
  const page = await fetchSupplierEvaluationPage({
    pageNum: 1,
    pageSize: 30,
    keyword: keyword?.trim() || undefined
  })
  const map = new Map<string, { id: string; name: string }>()
  for (const item of page?.records || []) {
    const id = toId((item as any).enterpriseId)
    if (!id || map.has(id)) continue
    map.set(id, {
      id,
      name: (item as any).enterpriseName || id
    })
  }
  return Array.from(map.values())
}

/** 询价匹配模板 */
export async function matchTemplateForInquiry(
  supplierId: string,
  templateType: QuotationTemplateType
): Promise<QuotationTemplate | null> {
  const detail = await matchSupplierQuotationTemplate({ supplierId, templateType })
  if (!detail) return null
  return mapTemplateDetail(detail)
}

/** 分页查询报价 */
export async function fetchQuotations(params: {
  pageNum: number
  pageSize: number
  keyword?: string
  supplierId?: string
  status?: string
}): Promise<{ records: SupplierQuotation[]; total: number }> {
  const page = await pageSupplierQuotation({
    pageNum: params.pageNum,
    pageSize: params.pageSize,
    keyword: params.keyword,
    supplierId: params.supplierId,
    status: params.status
  })
  return {
    records: (page?.records || []).map((item: any) => ({
      id: toId(item.id),
      quotationNo: item.quotationNo,
      supplierId: toId(item.supplierId),
      supplierName: item.supplierName || '',
      templateId: toId(item.templateId),
      templateType: 'FCL',
      templateName: '',
      status: item.status === 'SUBMITTED' ? 'submitted' : item.status === 'ABANDONED' ? 'abandoned' : 'submitted',
      fieldData: item.fieldData || {},
      submittedAt: item.submitDate || '',
      createdAt: item.dateCreated || ''
    })),
    total: page?.total || 0
  }
}

/** 报价详情 */
export async function fetchQuotationDetail(id: string): Promise<SupplierQuotation | null> {
  const detail = await getSupplierQuotation(id)
  if (!detail) return null
  return {
    id: toId(detail.id),
    quotationNo: detail.quotationNo,
    supplierId: toId(detail.supplierId),
    supplierName: detail.supplierName || '',
    templateId: toId(detail.templateId),
    templateType: 'FCL',
    templateName: detail.template?.templateName || '',
    status: detail.status === 'ABANDONED' ? 'abandoned' : 'submitted',
    fieldData: (detail.fieldData || {}) as any,
    submittedAt: detail.submitDate || '',
    createdAt: detail.dateCreated || ''
  }
}

/** 提交报价 */
export async function submitQuotation(data: {
  id?: string
  templateId: string
  supplierId: string
  supplierName?: string
  accessToken: string
  fieldData: Record<string, any>
  inquiryId?: string
  inquiryNo?: string
  idempotentKey?: string
}): Promise<boolean> {
  return submitSupplierQuotation({
    id: data.id,
    templateId: data.templateId,
    supplierId: data.supplierId,
    supplierName: data.supplierName,
    accessToken: data.accessToken,
    fieldData: data.fieldData,
    inquiryId: data.inquiryId,
    inquiryNo: data.inquiryNo,
    idempotentKey: data.idempotentKey
  })
}

/** 保存草稿 */
export async function saveQuotationDraft(data: {
  id?: string
  templateId: string
  supplierId: string
  supplierName?: string
  accessToken: string
  fieldData: Record<string, any>
}): Promise<boolean> {
  return saveSupplierQuotationDraft(data)
}

/** 放弃报价 */
export async function abandonQuotation(id: string): Promise<boolean> {
  return abandonSupplierQuotation(id)
}

/** 费用下拉选项（存 ID，展示名称） */
export interface FeeOption {
  value: string
  label: string
}

/** 从费用管理接口构造费用选项 */
function toFeeOptions(list: FeeManagementVO[] | null | undefined): FeeOption[] {
  const map = new Map<string, FeeOption>()
  for (const item of list || []) {
    const id = item?.id == null ? '' : String(item.id)
    const label = (item.feeNameCn || item.feeNameEn || item.feeCode || '').trim()
    if (!id || !label || map.has(id)) continue
    map.set(id, { value: id, label })
  }
  return Array.from(map.values())
}

/** 获取费用选项（对接 mdm 费用管理，value=费用ID） */
export async function fetchFeeOptions(keyword?: string): Promise<FeeOption[]> {
  const key = keyword?.trim()
  if (key) {
    const list = await feeManagementApi.search(key, undefined, 1)
    return toFeeOptions(list)
  }
  const list = await feeManagementApi.list({
    enabled: 1,
    pageNum: 1,
    pageSize: 500
  })
  return toFeeOptions(list)
}

/** @deprecated 兼容旧调用：仅返回费用名称 */
export async function fetchFeeNames(keyword?: string): Promise<string[]> {
  const options = await fetchFeeOptions(keyword)
  return options.map(item => item.label)
}

/** 按 token 校验并加载报价（供应商 H5） */
export async function validateToken(token: string): Promise<{
  valid: boolean
  quotationId: string
  templateId: string
  templateName: string
  templateType: QuotationTemplateType
  fields: TemplateFieldConfig[]
  supplierId: string
  supplierName: string
  fieldData: Record<string, any>
  status: string
} | null> {
  if (!token) return null
  const detail = await getSupplierQuotationByToken(token)
  if (!detail) return null
  const fields = (detail.template?.fields || []).map(mapField)
  return {
    valid: true,
    quotationId: toId(detail.id),
    templateId: toId(detail.templateId),
    templateName: detail.template?.templateName || '',
    templateType: (detail.template?.templateTypes?.[0] || 'FCL') as QuotationTemplateType,
    fields,
    supplierId: toId(detail.supplierId),
    supplierName: detail.supplierName || '',
    fieldData: (detail.fieldData || {}) as any,
    status: detail.status || 'PENDING'
  }
}
